import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { API_USER_FIELD_CONFIG } from 'constants/apiRoutes';
import http from 'utility/http';
import PageContainer from '../../shared/pageContainer';
import { APP_PAGES } from '../../../constants/pages';
import { useTranslations } from '../../../utility/useTranslations';
import CustomButton from '../../shared/customButton';
import CustomFormDrawer from '../../shared/customFormDrawer';
import {
  getFields,
  groupUserFieldsByCategories,
  prepareInitialData,
} from './config';
import { isArrayEmpty } from '../../../utility/helpers';
import CustomCheckbox from '../../shared/customCheckbox';
import { showSuccessMessage } from '../../../utility/uiUtils';
import { sticky } from '../../../constants/helperCssRules';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import { ORG_SETTINGS_NAVIGATION_ITEMS } from '../../../constants/organizationSettings';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { hasAuthPermissionCheck } from '../../../utility/permissions';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  userFieldSettingsForm: {
    display: 'grid',
    gridColumnGap: 24,
    gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
    gridTemplateAreas: '"informations"',
  },
  informations: {
    gridArea: 'informations',
  },
  headerContent: {
    borderBottom: `1px solid ${primary.bluish8}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: spacing(2),
  },
  columnWrapper: {
    marginTop: spacing(6),
    paddingBottom: spacing(6),
    marginBottom: spacing(5),
    borderBottom: `1px solid ${primary.bluish7}`,
    display: 'flex',
    justifyContent: 'space-between',
  },
  wrapper: {
    height: 40,
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  checkboxWrapper: {
    height: '100%',
    textAlign: 'center',
    display: 'grid',
    gridTemplateColumns: '139px 139px',
    gap: 4,
  },
  labelWrapper: {
    width: 512,
  },
  editButton: {
    maxHeight: 22,
    padding: spacing(1, 2, 1, 2),
    fontSize: 12,
    lineHeight: '14px',
    fontFamily: 'ProximaNova-Bold',
  },
  checkboxPreviewWrapper: {
    backgroundColor: primary.bluish9,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    cursor: 'default',
  },
  customCheckbox: {
    cursor: 'default',
  },
  grid: {
    gap: 4,
    display: 'grid',
  },
  categories: {
    marginBottom: spacing(7),
  },
  categoryTitle: {
    marginBottom: spacing(6),
  },
  informationList: {
    gap: 4,
    display: 'grid',
  },
  description: {
    paddingBottom: spacing(4),
    ...sticky(primary.white, 170),
  },
}));

const OrganizationSettingsPeopleInfoPage = ({
  organizationSettings,
  accessControl,
  navigate,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.PEOPLE_INFO_SETTINGS);

  const canAccessFeatures = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_FEATURES_MANAGE,
    accessControl
  );

  const canAccessPermissions = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_PERMISSIONS_MANAGE,
    accessControl
  );

  const canAccessDocuments = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_DOCUMENTS_MANAGE,
    accessControl
  );

  const navigationItems = useMemo(() => {
    return ORG_SETTINGS_NAVIGATION_ITEMS.reduce((acc, navItem) => {
      if (
        (!canAccessPermissions && navItem.translationKey === 'permissions') ||
        (!canAccessFeatures && navItem.translationKey === 'features') ||
        (!canAccessDocuments && navItem.translationKey === 'documents')
      ) {
        return acc;
      }
      return [
        ...acc,
        {
          ...navItem,
          name: translations.navigationItems[navItem.translationKey],
        },
      ];
    }, []);
  }, [
    translations.navigationItems,
    canAccessPermissions,
    canAccessDocuments,
    canAccessFeatures,
  ]);

  const [isLoading, setIsLoading] = useState(true);
  const [isEditDrawerOpened, setIsEditDrawerOpened] = useState(false);
  const [userFields, setUserFields] = useState([]);
  const hasFields = !isArrayEmpty(userFields);

  const categories = useMemo(
    () =>
      groupUserFieldsByCategories(userFields).map(category => ({
        ...category,
        name: translations.categories[category.translationKey],
      })),
    [userFields, translations.categories]
  );

  const getInitialData = useCallback(async () => {
    const { data } = await http.get(API_USER_FIELD_CONFIG);
    setUserFields(data);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const handleEditOpen = () => {
    setIsEditDrawerOpened(true);
  };

  const handleCloseEditDrawer = () => {
    setIsEditDrawerOpened(false);
  };

  const handleEdit = async values => {
    const { informations } = values;
    const { data } = await http.patch(API_USER_FIELD_CONFIG, informations);
    showSuccessMessage(translations.successMessages.updatedInformation);
    setUserFields(data);
  };

  return (
    <PageContainer
      {...rest}
      dispatch={dispatch}
      accessControl={accessControl}
      translations={translations}
      organizationSettings={organizationSettings}
      navigate={navigate}
      navigationItems={navigationItems}
      shouldPassProps={false}
    >
      {!isLoading && (
        <div>
          <Typography className={classes.description} variant="body1">
            {translations.description}
          </Typography>
          <div className={classes.headerContent}>
            <Typography variant="h3">{translations.subtitle}</Typography>
            <CustomButton
              className={classes.editButton}
              type="withTextLightRounded"
              onClick={handleEditOpen}
            >
              {translations.edit}
            </CustomButton>
          </div>
          <div className={classes.columnWrapper}>
            <div className={classes.labelWrapper}>
              <Typography variant="body2">{translations.nameLabel}</Typography>
            </div>
            <div className={classes.checkboxWrapper}>
              <Typography variant="body2">
                {translations.visibleLabel}
              </Typography>
              <Typography variant="body2">
                {translations.sensitiveLabel}
              </Typography>
            </div>
          </div>
          {hasFields &&
            categories.map(category => (
              <div key={category.key} className={classes.categories}>
                <Typography className={classes.categoryTitle} variant="h5">
                  {category.name}
                </Typography>
                <div className={classes.informationList}>
                  <div className={classes.grid}>
                    {category.fields.map(uField => (
                      <div key={uField.name} className={classes.wrapper}>
                        <div className={classes.labelWrapper}>
                          <TextBoxWithTooltip
                            variant="subtitle2"
                            text={uField.label}
                          />
                        </div>
                        <div className={classNames(classes.checkboxWrapper)}>
                          <div className={classes.checkboxPreviewWrapper}>
                            <CustomCheckbox
                              customCheckBoxRoot={classes.customCheckbox}
                              customCheckboxClass={classes.customCheckbox}
                              customCheckedCheckboxClass={
                                classes.customCheckbox
                              }
                              isChecked={uField.is_visible}
                              onChange={() => {}}
                              disabled
                            />
                          </div>
                          <div className={classes.checkboxPreviewWrapper}>
                            <CustomCheckbox
                              customCheckBoxRoot={classes.customCheckbox}
                              customCheckboxClass={classes.customCheckbox}
                              customCheckedCheckboxClass={
                                classes.customCheckbox
                              }
                              isChecked={uField.is_sensitive}
                              onChange={() => {}}
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
      <CustomFormDrawer
        customContentClass={classes.userFieldSettingsForm}
        translations={translations.userFieldSettingsForm}
        isOpened={isEditDrawerOpened}
        initialData={prepareInitialData(userFields)}
        fields={getFields(classes)}
        onClose={handleCloseEditDrawer}
        onSave={handleEdit}
        isInitialValid
        isMiddleWidth
        hideDelete
        hasCancelButton
      />
    </PageContainer>
  );
};

OrganizationSettingsPeopleInfoPage.propTypes = {
  organizationSettings: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(OrganizationSettingsPeopleInfoPage);
